import { useState } from 'react';
import { Box, Text, Tooltip, Icon, Link } from '@nimbus-ds/components';
import { InfoCircleIcon } from '@nimbus-ds/icons';
import { FormField } from '@nimbus-ds/patterns';
import { Trans } from 'react-i18next';
import { InvoicesVariantRequestDto } from '@tiendanube/common';
import { Stack } from 'commons/components';
import { useTranslationCatalog } from 'domains/Catalog/hooks';
import { OnChangeToAllVariants } from 'domains/Catalog/Products/pages/components/Variants/types';
import ApplyValuesToAllVariants from '../../../ApplyValuesToAllVariants';

interface NcmAndCestProps {
  valueNcm?: string;
  valueCest?: string;
  onChange: (data: InvoicesVariantRequestDto) => void;
  onChangeToAllVariants?: OnChangeToAllVariants;
}

const MAX_LENGTH_NCM = 8;
const MAX_LENGTH_CEST = 7;

function NcmAndCest({
  valueNcm,
  valueCest,
  onChange,
  onChangeToAllVariants,
}: Readonly<NcmAndCestProps>) {
  const t = useTranslationCatalog('products.detail.invoiceSettings');
  const [error, setError] = useState<{ ncm?: string; cest?: string }>({});

  const processInputValue = (name: string, value: string) => {
    const maxLength = name === 'cest' ? MAX_LENGTH_CEST : MAX_LENGTH_NCM;
    const numericValue = value.replace(/\D/g, '').slice(0, maxLength);
    return { numericValue, maxLength };
  };

  const validateInput = (
    name: string,
    numericValue: string,
    maxLength: number,
  ) => {
    // Show error only if field is not empty and incomplete
    if (numericValue.length > 0 && numericValue.length < maxLength) {
      setError((prev) => ({
        ...prev,
        [name]: t('helpTextError', {
          label: t(name === 'ncm' ? 'titleNcm' : 'titleCest'),
          maxLength,
        }),
      }));
    } else {
      setError((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const { numericValue, maxLength } = processInputValue(name, value);

    // Notify parent
    onChange({ [name]: numericValue || null });

    // Clear error only if value is empty or complete
    if (numericValue.length === 0 || numericValue.length === maxLength) {
      setError((prev) => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    const { numericValue, maxLength } = processInputValue(name, value);
    validateInput(name, numericValue, maxLength);
  };

  return (
    <Stack.Item>
      <Box display="grid" gridTemplateColumns="1fr 1fr" gridGap="2">
        <Stack column align="flex-start" gap="1">
          <FormField.Input
            type="number"
            label={t('titleNcm')}
            helpText={error.ncm || ''}
            showHelpText
            id="input-ncm"
            value={valueNcm}
            name="ncm"
            onChange={handleChange}
            onBlur={handleBlur}
            appearance={error.ncm ? 'warning' : 'none'}
          />
          {onChangeToAllVariants && (
            <ApplyValuesToAllVariants
              value={valueNcm}
              onChange={onChangeToAllVariants(
                'invoiceSettings',
                '',
                undefined,
                {
                  ncm: valueNcm,
                },
              )}
            />
          )}
          <Box>
            <Text fontSize="caption">
              <Trans
                t={t}
                i18nKey="helpTextNcm"
                components={{
                  helpLinkNcm: (
                    <Link
                      as="a"
                      appearance="neutral"
                      href={t('helpLinkNcm')}
                      target="_blank"
                      textDecoration="underline"
                      fontSize="caption"
                    >
                      {' '}
                    </Link>
                  ),
                }}
              />
            </Text>
          </Box>
        </Stack>
        <Stack column align="flex-start" gap="1">
          <Stack gap="1">
            <Text fontSize="caption">{t('titleCest')}</Text>
            <Tooltip content={t('tooltipCest')} position="right">
              <Icon
                color="currentColor"
                source={<InfoCircleIcon size={12} />}
              />
            </Tooltip>
          </Stack>

          <FormField.Input
            type="number"
            helpText={error.cest || ''}
            showHelpText
            id="input-cest"
            value={valueCest}
            name="cest"
            onChange={handleChange}
            onBlur={handleBlur}
            appearance={error.cest ? 'warning' : 'none'}
          />
          {onChangeToAllVariants && (
            <ApplyValuesToAllVariants
              value={valueCest}
              onChange={onChangeToAllVariants(
                'invoiceSettings',
                '',
                undefined,
                {
                  cest: valueCest,
                },
              )}
            />
          )}
        </Stack>
      </Box>
    </Stack.Item>
  );
}

export default NcmAndCest;
